import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AudioRecorder from './AudioRecorder';
import BulkUploadQueue from './BulkUploadQueue';
import './AudioUploader.css';
import {
  convertToMp3,
  needsConversion,
  convertVideoToAudio
} from '../utils/audioConverter';
import analytics from '../utils/analytics';

// Configuration - use the correct endpoint from your working code
const API_CONFIG = {
  TRANSCRIBE_ENDPOINT: '/transcribe',
  MAX_CONCURRENT_UPLOADS: 3,
};

const AudioUploader = ({
  onFileUpload,
  onTranscribe,
  onBulkTranscribe,
  isLoading,
  isAudioLoading,
  hasAudio,
  hasTranscript,
  showJsonUpload = false,
  showRecorder = true,
  onDownload,
  onDownloadTxt,
  isAuthenticated = false,
  showConfirm,
  onLiveTranscriptUpdate,
  onLiveSessionComplete,
  enableBulkUpload = false,
  autoRecord = false
}) => {
  const navigate = useNavigate();
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null); // Separate ref for folder upload
  const [uploadQueue, setUploadQueue] = useState([]);
  const abortControllersRef = useRef(new Map());
  
  // ADD THIS: State for folder upload setting
  const [enableFolderUpload, setEnableFolderUpload] = useState(false);

  // Initialize queue from localStorage only if authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllUploads();
      return;
    }

    const savedQueue = localStorage.getItem('uploadQueue');
    if (savedQueue) {
      try {
        const parsedQueue = JSON.parse(savedQueue);
        const sanitizedQueue = parsedQueue.map(item => ({
          ...item,
          file: null
        })).filter(item => item.status !== 'pending');
        
        setUploadQueue(sanitizedQueue);
        console.log('Restored upload queue:', sanitizedQueue.length, 'items');
      } catch (error) {
        console.error('Error loading queue:', error);
        localStorage.removeItem('uploadQueue');
      }
    }
  }, [isAuthenticated]);

  // ADD THIS: Load folder upload setting from preferences
  useEffect(() => {
    const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
    setEnableFolderUpload(preferences.enableFolderUpload === true);
  }, []);

  // Save queue to localStorage
  useEffect(() => {
    if (isAuthenticated && uploadQueue.length > 0) {
      const serializableQueue = uploadQueue.map(({ file, ...rest }) => rest);
      localStorage.setItem('uploadQueue', JSON.stringify(serializableQueue));
    } else if (!isAuthenticated) {
      localStorage.removeItem('uploadQueue');
    }
  }, [uploadQueue, isAuthenticated]);

  // Clear all uploads
  const clearAllUploads = useCallback(() => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current.clear();
    setUploadQueue([]);
    localStorage.removeItem('uploadQueue');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllersRef.current.forEach(controller => controller.abort());
    };
  }, []);

  const promptLogin = useCallback(async (action) => {
    if (!isAuthenticated) {
      if (!showConfirm) {
        const confirmed = window.confirm(`Please log in to ${action}. Would you like to go to the login page?`);
        if (confirmed) navigate('/auth');
        return true;
      }

      const confirmed = await showConfirm(
        'Login Required',
        `Please log in to ${action}. Would you like to go to the login page?`,
        { confirmText: 'Go to Login', cancelText: 'Cancel' }
      );
      if (confirmed) navigate('/auth');
      return true;
    }
    return false;
  }, [navigate, showConfirm, isAuthenticated]);

  const getFileTypeFromExtension = (filename) => {
    const extension = filename.toLowerCase().split('.').pop();
    const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'wma', 'opus'];
    const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', '3gp', 'ogv', 'mpg', 'mpeg', 'ts', 'vob'];
    
    if (audioExtensions.includes(extension)) return 'audio';
    if (videoExtensions.includes(extension)) return 'video';
    if (extension === 'json') return 'json';
    return null;
  };

  const generateFileId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle view transcript - navigate to transcript page
  const handleViewTranscript = useCallback((transcriptId, transcriptData) => {
    if (!transcriptId) {
      console.error('No transcript ID available');
      return;
    }

    // Pass transcript data via state to the transcript page
    navigate(`/?transcript=${transcriptId}`, { 
      state: { 
        transcriptData: transcriptData,
        transcriptId: transcriptId
      }
    });
  }, [navigate]);

  // Update queue item
  const updateQueueItem = useCallback((fileId, updates) => {
    setUploadQueue(prev =>
      prev.map(item =>
        item.id === fileId ? { ...item, ...updates } : item
      )
    );
  }, []);

  // Get auth token
  const getAuthToken = () => localStorage.getItem('authToken');

  // Process single file: convert → transcribe (SYNCHRONOUS - no polling)
  const processFile = useCallback(async (queueItem) => {
    const { id: fileId, file, name, type } = queueItem;
    
    try {
      let processedFile = file;

      // Step 1: Convert video to audio if needed
      if (type === 'video') {
        updateQueueItem(fileId, { 
          status: 'converting', 
          progress: 5,
          statusText: 'Initializing video converter...'
        });

        try {
          updateQueueItem(fileId, { 
            progress: 10,
            statusText: 'Loading video converter (first time may take 10s)...'
          });

          processedFile = await convertVideoToAudio(file);
          
          if (!processedFile || processedFile.size === 0) {
            throw new Error('Video has no audio track');
          }
          
          updateQueueItem(fileId, { 
            progress: 30,
            statusText: 'Video converted to audio successfully'
          });
        } catch (error) {
          console.error('Video conversion error:', error);
          throw new Error(`Video conversion failed: ${error.message}`);
        }
      }

      // Step 2: Convert audio format if needed (to MP3)
      if (needsConversion(processedFile)) {
        updateQueueItem(fileId, { 
          status: 'converting', 
          progress: type === 'video' ? 35 : 10,
          statusText: 'Converting to MP3...'
        });

        try {
          processedFile = await convertToMp3(processedFile);
          
          updateQueueItem(fileId, { 
            progress: type === 'video' ? 45 : 25,
            statusText: 'Converted to MP3'
          });
        } catch (error) {
          throw new Error(`Audio conversion failed: ${error.message}`);
        }
      }

      // Step 3: Upload and transcribe in one SYNC request
      updateQueueItem(fileId, { 
        status: 'uploading', 
        progress: type === 'video' ? 50 : 30,
        statusText: 'Uploading and transcribing...'
      });

      const formData = new FormData();
      formData.append('audio', processedFile);
      
      // Set transcript name from filename (without extension)
      const transcriptName = name.replace(/\.[^/.]+$/, '');
      formData.append('transcriptName', transcriptName);
      
      // Add diarization preference
      const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
      console.log("DEBUG: Checking diarization setting from localStorage:", preferences, "enableDiarization value:", preferences.enableDiarization, "type:", typeof preferences.enableDiarization);
      console.log("DEBUG: Checking diarization setting from localStorage:", preferences, "enableDiarization value:", preferences.enableDiarization, "type:", typeof preferences.enableDiarization);
      if (preferences.enableDiarization === true) {
        formData.append('enableDiarization', 'true');
      }

      // Create abort controller
      const abortController = new AbortController();
      abortControllersRef.current.set(fileId, abortController);

      console.log(`[Upload] Sending to ${API_CONFIG.TRANSCRIBE_ENDPOINT}: ${name}`);
      const response = await fetch(API_CONFIG.TRANSCRIBE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: formData,
        signal: abortController.signal
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log(`[Upload] Success response for ${name}:`, result);
      
      // Extract transcript ID from various possible response formats
      let transcriptData, transcriptId;
      
      if (result.id) {
        transcriptId = result.id;
        transcriptData = result;
      } else if (result.transcript && result.transcript.id) {
        transcriptId = result.transcript.id;
        transcriptData = result.transcript;
      } else if (result.transcriptId) {
        transcriptId = result.transcriptId;
        transcriptData = result;
      } else if (result.transcript_id) {
        transcriptId = result.transcript_id;
        transcriptData = result;
      } else if (result.data && result.data.id) {
        transcriptId = result.data.id;
        transcriptData = result.data;
      } else {
        transcriptData = result;
        
        // Generate a fallback ID if no UUID found
        transcriptId = `transcript-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        console.warn('No transcript ID found in response, using fallback:', transcriptId);
      }
      
      console.log(`Extracted transcript ID: ${transcriptId}`);
      
      // Update to completed status
      updateQueueItem(fileId, { 
        status: 'completed', 
        progress: 100,
        transcript: transcriptData,
        transcriptId: transcriptId,
        completedAt: new Date().toISOString(),
        statusText: 'Completed - Click View to open',
        transcriptUrl: transcriptId ? `/?transcript=${transcriptId}` : null
      });

      console.log(`File ${name} completed with transcript ID: ${transcriptId}`);
      abortControllersRef.current.delete(fileId);

    } catch (error) {
      if (error.name === 'AbortError') {
        updateQueueItem(fileId, { 
          status: 'cancelled',
          error: 'Upload cancelled',
          progress: 0,
          statusText: 'Cancelled'
        });
      } else {
        console.error(`Error processing ${name}:`, error);
        updateQueueItem(fileId, { 
          status: 'failed',
          error: error.message,
          progress: 0,
          statusText: `Failed: ${error.message.substring(0, 50)}${error.message.length > 50 ? '...' : ''}`
        });
      }
      
      abortControllersRef.current.delete(fileId);
    }
  }, [updateQueueItem]);

  // Auto-process pending files
  useEffect(() => {
    if (!isAuthenticated) return;

    const pendingFiles = uploadQueue.filter(item => item.status === 'pending');
    const activeUploads = uploadQueue.filter(item => 
      ['converting', 'uploading'].includes(item.status)
    ).length;

    if (pendingFiles.length > 0 && activeUploads < API_CONFIG.MAX_CONCURRENT_UPLOADS) {
      const filesToProcess = pendingFiles.slice(0, API_CONFIG.MAX_CONCURRENT_UPLOADS - activeUploads);
      filesToProcess.forEach(file => processFile(file));
    }
  }, [uploadQueue, isAuthenticated, processFile]);

  // Handle file selection for regular file upload
  const handleFileChange = useCallback((event) => {
    setUploadError(null);
    
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (!isAuthenticated) {
      promptLogin('upload files');
      event.target.value = '';
      return;
    }

    const supportedFiles = files.filter(file => {
      const fileType = getFileTypeFromExtension(file.name);
      return fileType === 'audio' || fileType === 'video';
    });

    const unsupportedCount = files.length - supportedFiles.length;
    if (unsupportedCount > 0) {
      setUploadError(`${unsupportedCount} unsupported file(s) skipped. Only audio/video files are supported.`);
    }

    if (supportedFiles.length === 0) {
      setUploadError('No supported audio or video files found. Supported formats: MP3, WAV, OGG, M4A, AAC, FLAC, WMA, OPUS, MP4, MOV, AVI, MKV, WEBM, FLV, WMV, M4V, 3GP, OGV, MPG, MPEG, TS, VOB');
      event.target.value = '';
      return;
    }

    const newQueueItems = supportedFiles.map(file => ({
      id: generateFileId(),
      name: file.name,
      size: file.size,
      type: getFileTypeFromExtension(file.name),
      file: file,
      status: 'pending',
      progress: 0,
      error: null,
      statusText: 'Waiting...',
      addedAt: new Date().toISOString()
    }));

    console.log(`Adding ${newQueueItems.length} files to queue`);
    setUploadQueue(prev => [...prev, ...newQueueItems]);
    event.target.value = '';
  }, [isAuthenticated, promptLogin]);

  // Handle folder selection
  const handleFolderChange = useCallback((event) => {
    setUploadError(null);
    
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (!isAuthenticated) {
      promptLogin('upload files');
      event.target.value = '';
      return;
    }

    const supportedFiles = files.filter(file => {
      const fileType = getFileTypeFromExtension(file.name);
      return fileType === 'audio' || fileType === 'video';
    });

    const unsupportedCount = files.length - supportedFiles.length;
    if (unsupportedCount > 0) {
      setUploadError(`${unsupportedCount} unsupported file(s) in folder skipped. Only audio/video files are supported.`);
    }

    if (supportedFiles.length === 0) {
      setUploadError('No supported audio or video files found in the folder. Supported formats: MP3, WAV, OGG, M4A, AAC, FLAC, WMA, OPUS, MP4, MOV, AVI, MKV, WEBM, FLV, WMV, M4V, 3GP, OGV, MPG, MPEG, TS, VOB');
      event.target.value = '';
      return;
    }

    // Add folder name to each file for display
    const newQueueItems = supportedFiles.map(file => {
      const folderPath = file.webkitRelativePath || 'Unknown Folder';
      const folderName = folderPath.split('/')[0] || 'Folder';
      
      return {
        id: generateFileId(),
        name: file.name,
        displayName: `${folderName}/${file.name}`, // Show folder name in UI
        size: file.size,
        type: getFileTypeFromExtension(file.name),
        file: file,
        folder: folderName,
        status: 'pending',
        progress: 0,
        error: null,
        statusText: 'Waiting...',
        addedAt: new Date().toISOString()
      };
    });

    console.log(`Adding ${newQueueItems.length} files from folder to queue`);
    setUploadQueue(prev => [...prev, ...newQueueItems]);
    event.target.value = '';
  }, [isAuthenticated, promptLogin]);

  // Handle click on upload area - allows file selection for all users
  const handleUploadAreaClick = useCallback(() => {
    if (enableFolderUpload && isAuthenticated) {
      // Open FOLDER input when folder upload is enabled (requires auth)
      folderInputRef.current?.click();
    } else {
      // Open REGULAR file input (works for all users - login prompt happens after file selection)
      fileInputRef.current?.click();
    }
  }, [isAuthenticated, enableFolderUpload]);

  // Open folder upload
  const handleFolderUploadClick = useCallback((e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    folderInputRef.current?.click();
  }, [isAuthenticated, navigate]);

  // Drag and drop
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
    handleFileChange({ target: { files: event.dataTransfer.files } });
  }, [handleFileChange]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  const handleJsonUpload = useCallback((file) => {
    if (!isAuthenticated) {
      promptLogin('upload transcript files');
      return;
    }
    if (onFileUpload) onFileUpload(file, 'json');
  }, [onFileUpload, isAuthenticated, promptLogin]);

  const handleRecordingComplete = useCallback((file, timestamp) => {
    // Only authenticated users reach here (non-auth users see Try Rec-Chord button)
    if (onFileUpload) onFileUpload(file, 'audio', timestamp);
  }, [onFileUpload]);

  // Queue management
  const handleQueueRemove = useCallback((fileId) => {
    const controller = abortControllersRef.current.get(fileId);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(fileId);
    }
    setUploadQueue(prev => prev.filter(item => item.id !== fileId));
  }, []);

  const handleQueueClear = useCallback(() => {
    setUploadQueue(prev =>
      prev.filter(item => !['completed', 'failed', 'cancelled'].includes(item.status))
    );
  }, []);

  const handleQueueRetry = useCallback((fileId) => {
    const item = uploadQueue.find(i => i.id === fileId);
    if (item && item.status === 'failed' && item.file) {
      updateQueueItem(fileId, {
        status: 'pending',
        error: null,
        progress: 0,
        statusText: 'Retrying...'
      });
    } else if (item && !item.file) {
      setUploadError('Cannot retry: Original file not available. Please re-upload.');
    }
  }, [uploadQueue, updateQueueItem]);

  // Calculate stats
  const queueStats = {
    total: uploadQueue.length,
    pending: uploadQueue.filter(i => i.status === 'pending').length,
    converting: uploadQueue.filter(i => i.status === 'converting').length,
    uploading: uploadQueue.filter(i => i.status === 'uploading').length,
    completed: uploadQueue.filter(i => i.status === 'completed').length,
    failed: uploadQueue.filter(i => i.status === 'failed').length,
    totalSize: uploadQueue.reduce((sum, i) => sum + (i.size || 0), 0)
  };

  const isProcessing = queueStats.converting > 0 || queueStats.uploading > 0;

  return (
    <div className="audio-uploader">
      <div className="uploader-layout">
        <div className="upload-section-left">
          <div className={`upload-card ${hasAudio ? 'has-audio' : ''}`}>

            <div
              className={`drop-zone ${dragOver ? 'drag-over' : ''} ${!isAuthenticated ? 'auth-required' : ''}`}
              onDrop={isAuthenticated ? handleDrop : undefined}
              onDragOver={isAuthenticated ? handleDragOver : undefined}
              onDragLeave={isAuthenticated ? handleDragLeave : undefined}
              onClick={handleUploadAreaClick}
            >
              <div className="drop-content">
                <div className="drop-icon">
                  <span className="upload-icon-small">
                    {!isAuthenticated ? '📤' : '📁'}
                  </span>
                  <span className="upload-text-compact">
                    {isAuthenticated && enableFolderUpload ? (
                      // When folder upload is ENABLED - Show ONLY this
                      <>
                        Upload Folder <small> or click to browse files</small>
                      </>
                    ) : (
                      // When folder upload is DISABLED - Show ONLY this
                      <>
		      Click or drag file to upload
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            {showRecorder && (
              <div className="recording-section-right">
                <div className="recorder-section">
                  <AudioRecorder
                    onRecordingComplete={handleRecordingComplete}
                    onDownload={onDownload}
                    onDownloadTxt={onDownloadTxt}
                    hasTranscript={hasTranscript}
                    isAuthenticated={isAuthenticated}
                    onLoginPrompt={() => {
                      // Track unauthenticated record attempt in analytics
                      analytics.trackEvent('unauthenticated_record_attempt', {
                        category: 'authentication',
                        action: 'record_without_login',
                        location: 'home_page'
                      });
                      promptLogin('record audio');
                    }}
                    onLiveTranscriptUpdate={onLiveTranscriptUpdate}
                    onLiveSessionComplete={onLiveSessionComplete}
                    autoRecord={autoRecord}
                  />
                </div>
              </div>
            )}

            {/* Regular file input for individual files */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.wav,.ogg,.m4a,.aac,.flac,.wma,.opus,.mp4,.mov,.avi,.mkv,.webm,.flv,.wmv,.m4v,.3gp,.ogv,.mpg,.mpeg,.ts,.vob"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {/* Separate input for folder upload - requires login */}
            <input
              ref={folderInputRef}
              type="file"
              accept=".mp3,.wav,.ogg,.m4a,.aac,.flac,.wma,.opus,.mp4,.mov,.avi,.mkv,.webm,.flv,.wmv,.m4v,.3gp,.ogv,.mpg,.mpeg,.ts,.vob"
              multiple
              webkitdirectory="true"
              directory="true"
              mozdirectory="true"
              onChange={handleFolderChange}
              style={{ display: 'none' }}
              disabled={!isAuthenticated}
            />

            <div className="upload-actions">
              {showJsonUpload && (
                <button
                  className="btn-secondary"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.json';
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) handleJsonUpload(file);
                    };
                    input.click();
                  }}
                  disabled={!isAuthenticated}
                >
                  Upload JSON
                </button>
              )}
            </div>

            {uploadQueue.length > 0 && (
              <div className="queue-info">
                <p>
                  <strong>{queueStats.total}</strong> file(s) • 
                  Active: <strong>{queueStats.converting + queueStats.uploading}</strong> • 
                  Completed: <strong>{queueStats.completed}</strong>
                  {queueStats.failed > 0 && <> • Failed: <strong className="text-error">{queueStats.failed}</strong></>}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Queue Section - Below the split layout */}
      {uploadQueue.length > 0 && (
        <div className="queue-section">
          <div className="queue-header">
            <h3>Upload & Processing Queue</h3>
            <div className="queue-stats">
              <span className="stat-item">Total: {queueStats.total}</span>
              <span className="stat-item">Size: {formatFileSize(queueStats.totalSize)}</span>
            </div>
            <div className="queue-actions">
              <button
                className="btn-small"
                onClick={handleQueueClear}
                disabled={isProcessing}
              >
                Clear Completed
              </button>
              <button
                className="btn-small btn-danger"
                onClick={clearAllUploads}
              >
                Clear All
              </button>
            </div>
          </div>
          
          {/* Updated BulkUploadQueue with onViewTranscript prop */}
          <BulkUploadQueue
            files={uploadQueue}
            onRemove={handleQueueRemove}
            onClear={handleQueueClear}
            onRetry={handleQueueRetry}
            onViewTranscript={handleViewTranscript}
            formatFileSize={formatFileSize}
          />
        </div>
      )}

      {uploadError && (
        <div className="error-alert">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{uploadError}</span>
          </div>
          <button 
            className="error-dismiss"
            onClick={() => setUploadError(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default AudioUploader;
